/* Contains the core functions for making http calls */
/*
  imports
*/
import axios from "axios";
import { useRouter } from "vue-router";
import { IDimensionsDataSaveQuestionResponse } from "../interfaces/dimensions-data-save-question-response.interface";
import { IStructureInAppNotification } from "../interfaces/in-app-notification.interface";
import { useAppStatus } from "../use/useAppStatus";
import { deviceResult } from "./useGetDeviceInfo";
import { useTimeslotComplete } from "../use/useTimeslotComplete";
import { API_BASE_URL, API_KEY } from "./useApiConfig";

/*
  declarations
*/

const { appStatus } = useAppStatus();
const timeslotCompleteComposable: any = useTimeslotComplete();

const environment: string = "local"; // "local" | "cloud"

const router = useRouter();

// In-memory cache for getDays to prevent redundant API calls
// Stores the resolved data (not the axios response object)
const daysCache = new Map<string, { data: any; timestamp: number }>();
const DAYS_CACHE_TTL = 5000; // 5 seconds

// In-flight Promise cache to deduplicate concurrent requests
const daysPending = new Map<string, Promise<any>>();

let server = API_BASE_URL;
if (environment === "cloud") {
  server = "https://us-central1-vernal-reality-329706.cloudfunctions.net";
}

// set global axios defaults for all requests
axios.defaults.headers.common["touchpoints-api-key"] = API_KEY;
axios.defaults.headers.common["Cache-Control"] = "no-store"; // Use 'no-store' to prevent caching issue on Safari browser,
axios.defaults.baseURL = server;

// log all axios errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Axios error:", error);
    return error;
  },
);

export async function prepareDiary(username: string) {
  const url = `/prepare_diary`;
  return await axios.post(url, { username: username });
}

/*
  Time-Slot
*/
export async function getDiary(user_name: string) {
  const userName = user_name;

  const url = `/get_diary/${userName}`;

  return await axios.get(url);
}
/*
  User Verification
*/
export async function verifyUser(user_name: string) {
  let url = "";
  let body = {};
  switch (environment) {
    case "local":
      url = `/user_verification`;
      body = {
        user_name: user_name, //'Dragos'
      };
      break;
    case "cloud":
      url = `/user_verification`;
      body = {
        user_name: user_name, //'Dragos'
      };
      break;
  }

  return axios
    .put(url, {
      user_name: user_name, //'Dragos'
    })
    .then((response) => {
      return response.data;
    });
}
/*
  User agreement log
*/
export async function AgreementLog(
  user_name: string,
  agreement_type: number,
  agreement_version: string,
  agreement_status: number,
) {
  return axios
    .put(`/agreement_log`, {
      user_name: user_name, //'Dragos'
      agreement_type: agreement_type,
      agreement_version: agreement_version,
      agreement_status: agreement_status,
    })
    .then((response) => {
      return response.data;
    });
}

/*
  Time-Slot
*/
export async function getTimeSlotSections(
  user_name: string,
  day_index: number,
) {
  const userName = user_name;
  const dayIndex = day_index;

  let url = "";

  if (environment === "local") {
    url = `/time_slots_sections/${userName}/${dayIndex}`;
  }

  /* switch (environment) {
      case "local":
        url = `/time-slots-all/${day_id}}`;
      break;
      case "cloud":
        url = `/time-slots-get-storage/?user_id=${encodedUsername}&project_name=${encodedProjectName}&day_index=${encodedDay_index}&time_slot_index=${encodedTime_slot_index}`;
      break;
    }*/

  return axios.get(url);
}

export async function getTimeSlotsByDayIndex(
  user_name: string,
  day_index: number,
) {
  return await axios.get(`/time_slots_all_on_day/${user_name}/${day_index}`);
}

/*
  handle Loading Screen appears when re-entering a diary on an old cached session. [ATD-1790]
*/
function clearOldCache() {
  window.localStorage.removeItem("userId");
  window.localStorage.removeItem("touchPointsLastLogin");
  window.localStorage.removeItem("username");
  window.localStorage.removeItem("touchPointsAuth");
  window.localStorage.removeItem("diaryStartDate");
  window.localStorage.removeItem("privacyAgree");
  window.localStorage.removeItem("trainingAgree");
  window.localStorage.removeItem("sleepTaskAgree");
  window.localStorage.removeItem("termAgree");
  window.localStorage.removeItem("dummyTimeslot");
  location.reload();
  router.push({ name: "login" });
}

export async function getDays(user_name: string) {
  const now = Date.now();
  const cached = daysCache.get(user_name);

  if (cached && now - cached.timestamp < DAYS_CACHE_TTL) {
    return cached.data;
  }

  // Return the existing in-flight Promise if one is already pending
  if (daysPending.has(user_name)) {
    return daysPending.get(user_name)!;
  }

  const fetchPromise = axios
    .get(`/get_days/${user_name}`)
    .then((response) => {
      daysCache.set(user_name, { data: response, timestamp: now });
      daysPending.delete(user_name);
      return response;
    })
    .catch((error) => {
      daysPending.delete(user_name);
      clearOldCache();
      throw error;
    });

  daysPending.set(user_name, fetchPromise);
  return fetchPromise;
}

/*
  update time slot status
*/
export async function updateTimeSlotStatus(
  user_name: string,
  day_index: number,
  time_slot_index: number,
  status: number,
  is_locked: number,
) {
  return axios
    .put(`/time_slot_update_status`, {
      user_name: user_name,
      day_index: day_index, //0,
      time_slot_index: time_slot_index, //0,
      status: status, //'3'
      is_locked: is_locked, //'0'
    })
    .then((response) => {
      console.log(response.data);
      timeslotCompleteComposable.checkIsDiaryComplete();
    });
}


// Randomize answers on Q36 emotion cache questions
function randomizeCategories(theHTML: string) {
  const xmlString = theHTML;
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const xmlDoc = parser.parseFromString(xmlString, "application/xml");
  const question = xmlDoc.querySelector('Question[QuestionName="Q36"]');
  if (question) {
    const categories = Array.from(question.getElementsByTagName("Category"));
    const lastCategoryIndex = categories.findIndex(
      (category) => category.getAttribute("Name") === "_14",
    );
    const secondLastCategoryIndex = categories.findIndex(
      (category) => category.getAttribute("Name") === "_13",
    );

    if (lastCategoryIndex !== -1 && secondLastCategoryIndex !== -1) {
      // Ensure the indices are ordered correctly
      const lastCategory = categories.splice(lastCategoryIndex, 1)[0];
      const secondLastCategory = categories.splice(
        secondLastCategoryIndex < lastCategoryIndex
          ? secondLastCategoryIndex
          : secondLastCategoryIndex - 1,
        1,
      )[0];

      // Randomize the categories except the last two
      for (let i = categories.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [categories[i], categories[j]] = [categories[j], categories[i]];
      }

      // Remove all existing Category elements
      categories.forEach((category) => question.removeChild(category));

      // Reinsert the randomized Category elements
      categories.forEach((category) => question.appendChild(category));

      // Reinsert the last two Category elements
      question.appendChild(secondLastCategory);
      question.appendChild(lastCategory);
    }
  }

  // Serialize the modified XML back to a string
  const newXmlString = serializer.serializeToString(xmlDoc);
  return newXmlString;
}

/*
  Dimensions Calls
*/
/*
  Get Question
*/
export async function getQuestion(
  time_slot_id: number,
  userName: string,
  dayIndex: number,
  timeSlotIndex: number,
  timeSlotIndexDataMap: string,
  timeSlotName: string,
  sectionIndex: number /*if is -1 then don`t use section in Server API*/,
  sectionName: string /*If empty then don`t use section in Server API*/,
  questionName: string,
  action: string,
  navType: string,
) {
  const response = axios.post(`/time_slots_get_question`, {
    userName: userName, //'Devtest',
    dayIndex: dayIndex, //0,
    timeSlotIndex: timeSlotIndex, //0,
    timeSlotIndexDataMap: timeSlotIndexDataMap,
    timeSlotName: timeSlotName,
    sectionIndex: sectionIndex,
    sectionName: sectionName,
    questionName: questionName, //'Example'
    action: action, //"Page"/"Question"
    navType: navType, //"Next"/"Stop",
    time_slot_id: time_slot_id,
  });

  if (sectionName === "EMOTION") {
    const htmlCacheContent = (await response).data;
    return randomizeCategories(htmlCacheContent);
  } else {
    return (await response).data;
  }
}
/*
export async function getQuestionLocal (
  time_slot_id: number,
  userName: string,
  dayIndex: number,
  timeSlotIndex: number,
  timeSlotIndexDataMap: string,
  timeSlotName: string,
  sectionIndex: number,
  sectionName: string,
  questionName: string,
  action: string,
  navType: string
) {

  let url = "";

  try {
    if(sectionName === "LOCATION")
    {
      url = '/xml/LocationPage.xml';
    }
    else if(sectionName === "WITH WHOM")
    {
      url = '/xml/WithWhomPage.xml';
    }
    else if(sectionName === "COMMUNICATION")
    {
      url = '/xml/CommunicationPage.xml';
    }
    else if(sectionName === "ACTIVITIES")
    {
      url = '/xml/ActivitiesPage.xml';
    }
    else if(sectionName === "MEDIA USE")
    {
      url = '/xml/MediaUsePage.xml';
    }
    else if(sectionName === "MOOD")
    {
      url = '/xml/MoodPage.xml';
    }
    else if(sectionName === "EMOTION")
    {
      url = '/xml/EmotionPage.xml';
    }

    const response = await fetch(url);
    const htmlCacheContent = await response.text();

    if(sectionName === "EMOTION"){
      return randomizeCategories(htmlCacheContent)
    }
    else{
      return htmlCacheContent;
    }

  } catch (error) {
    console.error('Error fetching HTML:', error);
  }
}
*/
export async function getSampleDataPage(
  time_slot_id: number,
  userName: string,
  dayIndex: number,
  timeSlotIndex: number,
  questionName: string,
  action: string,
) {
  return axios.post(`/time_slots_get_sampledatapage`, {
    userName: userName, //'Dragos',
    dayIndex: dayIndex, //0,
    timeSlotIndex: timeSlotIndex,
    questionName: questionName, //'Example'
    action: action, //"Page"/"Question",
    time_slot_id: time_slot_id,
  });
}

/*
  Get Adhoc Question
*/
export async function getAdhocQuestions(userName: string) {
  return axios.post(`/get_adhoc_questions`, {
    userName: userName,
  });
}

/*
  Post Adhoc answer
*/
export async function postAdhocAnswer(
  userName: string,
  questionsResponses: IDimensionsDataSaveQuestionResponse[],
  adhocEngine: string,
  adhocSession: string,
) {
  return axios.post(`/post_adhoc_questions`, {
    userName: userName, //'Jack',
    questionsResponses: questionsResponses,
    engine: adhocEngine,
    session: adhocSession,
  });
}

/*
  Save Question
*/
export async function saveQuestion(
  time_slot_id: number,
  userName: string,
  dayIndex: number,
  timeSlotIndex: number,
  sectionIndex: number,
  questionName: string,
  value: string,
  navType: string,
  totalNrOfSections: number,
) {
  // no-op (remove calls to the server for Dimensions related requests)
  return Promise.resolve({ data: true });

  if (appStatus.isTrainingMode) {
    return "";
  } else {
    return axios.post(`/time_slots_save_question`, {
      userName: userName, //'Dragos',
      dayIndex: dayIndex, //0,
      timeSlotIndex: timeSlotIndex, //0,
      sectionIndex: sectionIndex, //0
      questionName: questionName, //'Example'
      value: value, //"%7B_1%7D"
      navType: navType, //"Next"/"Stop",
      totalNrOfSections: totalNrOfSections,
      time_slot_id: time_slot_id,
    });
  }
}

/*
  Save Page
*/
export async function savePage(
  time_slot_id: number,
  userName: string,
  dayIndex: number,
  timeSlotIndex: number,
  sectionIndex: number,
  pageName: string,
  questionsResponses: IDimensionsDataSaveQuestionResponse[],
  navType: string,
  totalNrOfSections: number,
) {
  // no-op (remove calls to the server for Dimensions related requests)
  return Promise.resolve({ data: true });

  console.log(sectionIndex, totalNrOfSections);

  if (appStatus.isTrainingMode) {
    return "";
  } else {
    return axios.post(`/time_slots_save_page`, {
      userName: userName, //'Dragos',
      dayIndex: dayIndex, //0,
      timeSlotIndex: timeSlotIndex, //0,
      sectionIndex: sectionIndex, //0,
      pageName: pageName, //'Example'
      questionsResponses: questionsResponses, //'[{"question":"Q1","response":"%7B_1%7D"},{"question":"Q4","response":"%7B_2%7D"},{"question":"Q5","response":""},{"question":"Q6","response":""}]'
      navType: navType, //"Next"/"Stop",
      totalNrOfSections: totalNrOfSections,
      time_slot_id: time_slot_id,
    });
  }
}

/*
  update Diary Status (is_complete)
*/
export async function updateDiaryStatus(
  username: string,
  is_complete: boolean,
) {
  let url = "";
  let body = {};
  url = `/update_diary_status`;
  body = {
    username: username,
    is_complete: is_complete,
  };

  return axios
    .post(url, {
      username: username,
      is_complete: is_complete,
    })
    .then((response) => {
      return response.data;
    });
}

export async function updateUserCompleteOnboardingTime(user_name: string) {
  const url = `/update_user_onboarding_complete_time`;
  const body = {
    user_name: user_name,
  };

  return axios.post(url, body).then((response) => {
    return response.data;
  });
}

export async function updateDiaryLastAccessTime(user_name: string) {
  const url = `/update_diary_last_access_time`;
  const body = {
    user_name,
  };

  return axios.post(url, body).then((response) => {
    return response.data;
  });
}

export async function get_user_agreements(user_id: number) {
  const url = `/get_user_agreements/${user_id}`;

  return axios.get(url).then((response) => {
    return response.data;
  });
}

export function getTempQuestion(
  questionName: string,
  action: string,
  sectionName: string,
) {
  const url = `/time_slots_get_temp_question`;

  return axios
    .post(url, {
      questionName,
      action,
      sectionName,
    })
    .then((response) => {
      return response.data;
    });
}

export async function saveDevice(deviceInfo: deviceResult) {
  const url = `/save_device`;

  return axios.post(url, deviceInfo).then((response) => {
    return response.data;
  });
}

export async function register_subscription(
  subscription: any,
  username: string,
  deviceBrowserName: string,
  deviceModel: string,
  deviceOS: string,
  browserVersion: string,
  deviceType: string,
) {
  const url = `/register_subscription`;

  return axios
    .post(url, {
      subscription,
      username,
      deviceBrowserName,
      deviceModel,
      deviceOS,
      browserVersion,
      deviceType,
    })
    .then((response) => {
      console.log(response.data);
    });
}

export function check_for_reset_user(username: string) {
  const url = `/check_for_reset_user`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_main_menu_clicks(username: string) {
  const url = `/count_main_menu_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_survey_web_hplk_clicks(username: string) {
  const url = `/count_survey_web_hplk_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_how_video_clicks(username: string) {
  const url = `/count_how_video_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_timeline_buttons_clicks(username: string) {
  const url = `/count_timeline_buttons_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_jump_buttons_clicks(username: string) {
  const url = `/count_jump_buttons_clicks`;
  // Send and return promise immediately without waiting for response
  axios.post(url, { username });
  return Promise.resolve({ data: true });
}

export function count_clock_buttons_clicks(username: string) {
  const url = `/count_clock_buttons_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_extend_function_clicks(username: string) {
  const url = `/count_extend_function_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_tap_to_wake_up_clicks(username: string) {
  const url = `/count_tap_to_wake_up_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_notifications_clicks(username: string) {
  const url = `/count_notifications_clicks`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function count_reconnections(username: string) {
  const url = `/count_reconnections`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function used_day_interstitials(username: string) {
  const url = `/used_day_interstitials`;

  return axios
    .post(url, {
      username,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function extend_timeslot_answer(
  user_name: string,
  day_index: number,
  time_slot_index: number,
  section_indexes: number[],
) {
  const url = `/extend_timeslot_answers`;

  return axios
    .post(url, {
      user_name,
      day_index,
      time_slot_index,
      section_indexes,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function log_timeslot_answer(
  user_name: string,
  day_index: number,
  time_slot_index: number,
  summary_storage: string,
  section_index: number,
  section_answer: any,
) {
  const url = `/log_timeslot_answer`;

  return axios
    .post(url, {
      user_name,
      day_index,
      time_slot_index,
      summary_storage,
      section_index,
      section_answer,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export function add_timeslot_log(user_name: string, day_index: number, time_slot_index: number, section_index: number, section_answer: string, summary_storage: any) {
  const url = `/add_timeslot_log`;
  
  return axios
    .post(url, {
      user_name: user_name,
      day_index: day_index,
      time_slot_index: time_slot_index,
      section_index: section_index,
      section_answer: section_answer,
      summary_storage: summary_storage,
    })
    .then((response) => {
      console.log(response.data);
      return response.data;
    });
}

export async function getTimeslotSummaryStorage(
  user_name: string,
  day_index: number,
  time_slot_index: number,
) {
  return axios.post(`/timeslot_get_summary_storage`, {
    user_name: user_name,
    day_index: day_index,
    time_slot_index: time_slot_index,
  });
}

export async function getInAppNotifications(userName: string) {
  return axios.post(`/get_in_app_notifications_for_user`, {
    user_name: userName,
  });
}

export async function insertInAppNotifications(
  userName: string,
  inAppNotification: IStructureInAppNotification,
) {
  return axios.post(`/insert_new_in_app_notification`, {
    user_name: userName,
    in_app_notification: inAppNotification,
  });
}

export async function updateInAppNotifications(
  userName: string,
  backEndId: number,
  newViewedStatus: boolean,
) {
  return axios.post(`/update_in_app_notification_viewed_status`, {
    user_name: userName,
    in_app_notification_back_end_id: backEndId,
    new_viewed_status: newViewedStatus,
  });
}

export async function get_week_sections(user_name: string) {
  const timestamp = new Date().getTime(); // Generate a unique timestamp
  return await axios
    .get(`/get_week_time_slots_sections/${user_name}?_=${timestamp}`)
    .catch((error) => {
      clearOldCache();
    });
}
export async function get_week_time_slots(user_name: string) {
  const timestamp = new Date().getTime(); // Generate a unique timestamp
  return await axios
    .get(`/get_week_time_slots/${user_name}?_=${timestamp}`)
    .catch((error) => {
      clearOldCache();
    });
}

export async function set_section_complete_data(
  time_slot_id: number,
  sectionIndex: number,
) {
  return axios.post(`/set_section_complete_data`, {
    time_slot_id: time_slot_id,
    sectionIndex: sectionIndex,
  });
}

export async function count_faq_clicks(username: string) {
  return axios.post(`/count_faq_clicks`, {
    username: username,
  });
}

export async function count_put_asleep_clicks(username: string) {
  return axios.post(`/count_put_asleep_button_clicks`, {
    username: username,
  });
}

export async function count_platform_access(
  username: string,
  platform: string,
) {
  return axios.post(`/count_platform_access`, {
    username: username,
    platform: platform,
  }).then(() => {});
}

export async function create_onboarding_section_log(
  username: string,
  section_name: string,
  section_indx: number,
) {
  return axios.post(`/create_onboarding_section_log`, {
    username: username,
    section_name: section_name,
    section_indx: section_indx,
  });
}

export async function update_onboarding_section_log_exit_time(
  username: string,
  section_name: string,
  section_indx: number,
) {
  return axios.post(`/update_onboarding_section_log_exit_time`, {
    username: username,
    section_name: section_name,
    section_indx: section_indx,
  });
}

export async function log_section_enter(
  username: string,
  time_slot_id: number,
  section_indx: number,
) {
  return axios.post(`/log_section_enter`, {
    username: username,
    time_slot_id: time_slot_id,
    section_indx: section_indx,
  });
}

export async function log_section_exit(
  username: string,
  time_slot_id: number,
  section_indx: number,
) {
  return axios.post(`/log_section_exit`, {
    username: username,
    time_slot_id: time_slot_id,
    section_indx: section_indx,
  });
}

export async function mark_diary_access_log(
  username: string,
  browserName: string,
  browserVersion: string,
  deviceModel: string,
  deviceOS: string,
  deviceType: string,
  platform: string,
) {
  return axios.post(`/mark_diary_access_log`, {
    username: username,
    browserName: browserName,
    browserVersion: browserVersion,
    deviceModel: deviceModel,
    deviceOS: deviceOS,
    deviceType: deviceType,
    platform: platform,
  });
}

export async function mark_onboarding_task(
  username: string,
  task_name: string,
) {
  return axios.post(`/mark_onboarding_task`, {
    username: username,
    task_name: task_name,
  });
}

export async function get_user_onboarding_date(user_id: number) {
  return axios.post(`/get_user_onboarding_date`, {
    user_id: user_id,
  });
}

export async function correct_user_onboarding_date(user_id: number) {
  return axios.post(`/correct_user_onboarding_date`, {
    user_id: user_id,
  });
}

export function useTouchpointsHTTP() {
  return {
    prepareDiary,
    getDiary,
    verifyUser,
    AgreementLog,
    updateTimeSlotStatus,
    getTimeSlotSections,
    getTimeSlotsByDayIndex,
    getDays,
    getQuestion,
    saveQuestion,
    savePage,
    updateDiaryStatus,
    updateUserCompleteOnboardingTime,
    updateDiaryLastAccessTime,
    getSampleDataPage,
    getAdhocQuestions,
    postAdhocAnswer,
    saveDevice,
    register_subscription,
    check_for_reset_user,
    count_main_menu_clicks,
    count_survey_web_hplk_clicks,
    count_how_video_clicks,
    count_timeline_buttons_clicks,
    count_jump_buttons_clicks,
    count_clock_buttons_clicks,
    count_extend_function_clicks,
    count_tap_to_wake_up_clicks,
    count_notifications_clicks,
    count_reconnections,
    used_day_interstitials,
    extend_timeslot_answer,
    log_timeslot_answer,
    add_timeslot_log,
    getTimeslotSummaryStorage,
    getInAppNotifications,
    insertInAppNotifications,
    updateInAppNotifications,
    get_week_sections,
    get_week_time_slots,
    set_section_complete_data,
    count_faq_clicks,
    count_put_asleep_clicks,
    count_platform_access,
    create_onboarding_section_log,
    update_onboarding_section_log_exit_time,
    log_section_enter,
    log_section_exit,
    mark_diary_access_log,
    mark_onboarding_task,
    get_user_onboarding_date,
    correct_user_onboarding_date
  };
}
